import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TeamStanding } from '@sulis-liiga/common';

interface StandingsTableProps {
  standings: TeamStanding[];
  teamsToPromote: number;
  teamsToRelegate: number;
}

/** Format a difference with an explicit sign (+3, 0, -2). */
const withSign = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

/**
 * StandingsTable component
 *
 * A ranked division table. The default view is deliberately compact
 * (# · Team · P · W · L · Pts) so it fits a phone without horizontal scroll;
 * each row expands (progressive disclosure) to reveal the tie-break metrics —
 * game difference and point difference — matching the Divisions page. See
 * docs/requirements/GameRules.md §5.
 */
const StandingsTable: React.FC<StandingsTableProps> = ({
  standings,
  teamsToPromote,
  teamsToRelegate,
}) => {
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const toggleExpanded = (teamId: string) =>
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });

  const getRowBg = (index: number, total: number): string => {
    if (teamsToPromote > 0 && index < teamsToPromote) return 'bg-success-50';
    if (teamsToRelegate > 0 && index >= total - teamsToRelegate) return 'bg-danger-50';
    return '';
  };

  if (standings.length === 0) {
    return <div className="text-center py-8 text-gray-500">No teams in this division yet.</div>;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" aria-label="Division standings">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-10">#</th>
              <th className="text-left px-3 py-2 font-medium">Team</th>
              <th className="px-2 sm:px-3 py-2 font-medium text-center" title="Played">
                P
              </th>
              <th className="px-2 sm:px-3 py-2 font-medium text-center" title="Won">
                W
              </th>
              <th className="px-2 sm:px-3 py-2 font-medium text-center" title="Lost">
                L
              </th>
              <th className="px-2 sm:px-3 py-2 font-medium text-center font-bold" title="Points">
                Pts
              </th>
              <th className="px-1 py-2 w-8">
                <span className="sr-only">Tie-break details</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {standings.map((standing, index) => {
              const isOpen = expandedTeams.has(standing.teamId);
              const gd = standing.pointDifference ?? 0;
              const pd = (standing.pointsWon ?? 0) - (standing.pointsLost ?? 0);
              const rowBg = getRowBg(index, standings.length);
              return (
                <React.Fragment key={standing.teamId}>
                  <tr className={rowBg}>
                    <td className="px-3 py-2">{standing.rank}</td>
                    <td className="px-3 py-2 font-medium">
                      <Link
                        to={`/teams/${standing.teamId}`}
                        className="text-primary-600 hover:underline"
                      >
                        {standing.teamName}
                      </Link>
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-center">{standing.played}</td>
                    <td className="px-2 sm:px-3 py-2 text-center">{standing.won}</td>
                    <td className="px-2 sm:px-3 py-2 text-center">{standing.lost}</td>
                    <td className="px-2 sm:px-3 py-2 text-center font-semibold">
                      {standing.points}
                    </td>
                    <td className="px-1 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(standing.teamId)}
                        aria-expanded={isOpen}
                        aria-label={`Tie-break details for ${standing.teamName}`}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <svg
                          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className={rowBg}>
                      <td colSpan={7} className="px-3 pb-3 pt-0">
                        <div className="rounded-md bg-gray-50 px-3 py-2">
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                            <span>
                              <span className="text-gray-500">Game diff</span>{' '}
                              <span className="font-semibold text-gray-800">{withSign(gd)}</span>{' '}
                              <span className="text-gray-400">
                                ({standing.gamesWon ?? 0}–{standing.gamesLost ?? 0})
                              </span>
                            </span>
                            <span>
                              <span className="text-gray-500">Point diff</span>{' '}
                              <span className="font-semibold text-gray-800">{withSign(pd)}</span>{' '}
                              <span className="text-gray-400">
                                ({standing.pointsWon ?? 0}–{standing.pointsLost ?? 0})
                              </span>
                            </span>
                          </div>
                          <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
                            Teams level on points are ranked by game difference, then point
                            difference, then head-to-head.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      {(teamsToPromote > 0 || teamsToRelegate > 0) && (
        <div className="flex items-center gap-6 text-xs text-gray-600 px-4 py-3 border-t border-gray-100">
          {teamsToPromote > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-success-100 rounded-sm"></div>
              <span>Promotion zone (top {teamsToPromote})</span>
            </div>
          )}
          {teamsToRelegate > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-danger-100 rounded-sm"></div>
              <span>Relegation zone (bottom {teamsToRelegate})</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StandingsTable;
